import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Clock, Upload, Users, CalendarClock, Download, FileSpreadsheet,
  Building2,
  Loader2, Edit, Trash2, Plus, Fingerprint, UserCheck, AlertTriangle, RefreshCw,
  Wifi, Server, KeyRound, Copy, CheckCircle2, XCircle, Search, ChevronsUpDown, Mail
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${API_URL}/api/sistema-ponche`;

const hoy = () => new Date().toISOString().slice(0, 10);
const inicioMes = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const DIAS = [["0", "Lun"], ["1", "Mar"], ["2", "Mié"], ["3", "Jue"], ["4", "Vie"], ["5", "Sáb"], ["6", "Dom"]];

export default function SistemaPonchePage() {
  const { getAuthHeaders } = useAuth();
  const [tab, setTab] = useState("reportes");
  const [dash, setDash] = useState(null);

  const loadDash = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dashboard`, getAuthHeaders());
      setDash(r.data);
    } catch (e) { /* silent */ }
  }, [getAuthHeaders]);

  useEffect(() => { loadDash(); }, [loadDash]);

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Sidebar />
      <main className="flex-1 min-w-0 ml-0 md:ml-64 p-4 md:p-6 overflow-x-clip" data-testid="sistema-ponche-page">
        <div className="flex items-center gap-3 mb-6 rounded-2xl bg-gradient-to-r from-indigo-600 via-indigo-500 to-sky-500 p-5 shadow-lg">
          <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Fingerprint className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Sistema de Ponche</h1>
            <p className="text-sm text-white/85">Control de asistencia desde tus ponchadores (U160)</p>
          </div>
        </div>

        {dash && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KpiCard icon={Users} label="Empleados" value={dash.total_empleados} color="indigo" onClick={() => setTab("empleados")} testid="kpi-empleados" />
            <KpiCard icon={Clock} label="Marcaciones" value={dash.total_marcaciones} color="sky" onClick={() => setTab("reportes")} testid="kpi-marcaciones" />
            <KpiCard icon={UserCheck} label="Presentes hoy" value={dash.presentes_hoy} color="emerald" onClick={() => setTab("reportes")} testid="kpi-presentes" />
            <KpiCard icon={CalendarClock} label="Última importación"
              value={dash.ultima_importacion ? `${dash.ultima_importacion.insertadas} reg.` : "—"} color="amber" onClick={() => setTab("importar")} testid="kpi-ultima" />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4" data-testid="ponche-tabs">
            <TabsTrigger value="reportes" data-testid="tab-reportes">Reportes</TabsTrigger>
            <TabsTrigger value="conexion" data-testid="tab-conexion">Conexión</TabsTrigger>
            <TabsTrigger value="alertas" data-testid="tab-alertas">Alertas</TabsTrigger>
            <TabsTrigger value="envio-reportes" data-testid="tab-envio-reportes">Envío de Reportes</TabsTrigger>
            <TabsTrigger value="importar" data-testid="tab-importar">Importar (respaldo)</TabsTrigger>
            <TabsTrigger value="empleados" data-testid="tab-empleados">Empleados</TabsTrigger>
            <TabsTrigger value="departamentos" data-testid="tab-departamentos">Departamentos</TabsTrigger>
            <TabsTrigger value="horarios" data-testid="tab-horarios">Horarios</TabsTrigger>
          </TabsList>

          <TabsContent value="reportes"><ReportesTab getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="conexion"><ConexionTab getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="alertas"><AlertasTab getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="envio-reportes"><EnvioReportesTab getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="importar"><ImportarTab getAuthHeaders={getAuthHeaders} onImported={loadDash} /></TabsContent>
          <TabsContent value="empleados"><EmpleadosTab getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="departamentos"><DepartamentosTab getAuthHeaders={getAuthHeaders} /></TabsContent>
          <TabsContent value="horarios"><HorariosTab getAuthHeaders={getAuthHeaders} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const KPI_COLORS = {
  indigo: { bg: "from-indigo-500 to-indigo-600", ring: "hover:ring-indigo-300" },
  sky: { bg: "from-sky-500 to-sky-600", ring: "hover:ring-sky-300" },
  emerald: { bg: "from-emerald-500 to-emerald-600", ring: "hover:ring-emerald-300" },
  amber: { bg: "from-amber-500 to-amber-600", ring: "hover:ring-amber-300" },
};

function KpiCard({ icon: Icon, label, value, color = "indigo", onClick, testid }) {
  const c = KPI_COLORS[color] || KPI_COLORS.indigo;
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left w-full rounded-xl bg-gradient-to-br ${c.bg} text-white p-4 shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl ring-2 ring-transparent ${c.ring} focus:outline-none`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur-sm">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-xs text-white/80">{label}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
      </div>
    </button>
  );
}

/* ============ REPORTES ============ */
function ReportesTab({ getAuthHeaders }) {
  const [tipo, setTipo] = useState("diario");
  const [desde, setDesde] = useState(inicioMes());
  const [hasta, setHasta] = useState(hoy());
  const [numero, setNumero] = useState("");
  const [rows, setRows] = useState([]);
  const [totales, setTotales] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("todos");
  const [selDeps, setSelDeps] = useState([]);
  const [depList, setDepList] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { desde, hasta };
      if (numero.trim()) params.numero = numero.trim();
      const r = await axios.get(`${API}/reportes/${tipo}`, { ...getAuthHeaders(), params });
      setRows(r.data.items || []);
      setTotales(r.data.totales || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar el reporte");
    } finally { setLoading(false); }
  }, [tipo, desde, hasta, numero, getAuthHeaders]);

  useEffect(() => { cargar(); }, [tipo]); // eslint-disable-line

  useEffect(() => {
    axios.get(`${API}/departamentos`, getAuthHeaders())
      .then(r => setDepList((r.data.items || []).map(d => d.nombre).filter(Boolean)))
      .catch(() => {});
  }, [getAuthHeaders]);

  const minTardeStr = (m) => {
    const n = Math.round(parseFloat(m) || 0);
    if (!n) return "0";
    const h = Math.floor(n / 60);
    const mm = n % 60;
    return `${n} (${h}.${String(mm).padStart(2, "0")} h)`;
  };

  const exportar = async () => {
    try {
      const params = { desde, hasta };
      if (numero.trim()) params.numero = numero.trim();
      if (search.trim()) { params.q = search.trim(); params.campo = searchField; }
      if (selDeps.length) params.deps = selDeps.join(",");
      const r = await axios.get(`${API}/reportes/${tipo}/export/excel`,
        { ...getAuthHeaders(), params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `ponche_${tipo}_${desde}_${hasta}.xlsx`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error al exportar"); }
  };

  const cols = {
    diario: [["numero_empleado", "No."], ["nombre", "Nombre"], ["departamento", "Departamento"], ["fecha", "Fecha"],
      ["entrada", "Entrada"], ["salida", "Salida"], ["horas_laboradas", "Horas"],
      ["minutos_tarde", "Min. tarde"], ["estado", "Estado"], ["observaciones", "Observaciones"]],
    resumen: [["numero_empleado", "No."], ["nombre", "Nombre"], ["departamento", "Departamento"], ["dias_trabajados", "Días"],
      ["horas_laboradas", "Horas"], ["tardanzas", "Tardanzas"], ["incompletas", "Incompletas"],
      ["asistencia_pct", "Asistencia %"], ["puntualidad_pct", "Puntualidad %"]],
    ausentismo: [["numero_empleado", "No."], ["nombre", "Nombre"], ["departamento", "Departamento"], ["dias_laborables", "Días lab."],
      ["dias_presente", "Presente"], ["dias_ausente", "Ausente"], ["ausentismo_pct", "Ausentismo %"]],
  }[tipo];

  // Filtro en vivo + agrupación por departamento (y por día en el reporte diario)
  const displayRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let data = rows;
    if (selDeps.length) {
      const dset = new Set(selDeps.map(d => d.toLowerCase()));
      data = data.filter(r => dset.has(String(r.departamento || "").toLowerCase()));
    }
    if (q) {
      data = data.filter(r => {
        const dep = String(r.departamento || "").toLowerCase();
        const nom = String(r.nombre || "").toLowerCase();
        const num = String(r.numero_empleado || "").toLowerCase();
        const est = String(r.estado || "").toLowerCase();
        if (searchField === "departamento") return dep.includes(q);
        if (searchField === "nombre") return nom.includes(q);
        if (searchField === "numero") return num.includes(q);
        if (searchField === "estado") return est.includes(q);
        return dep.includes(q) || nom.includes(q) || num.includes(q) || est.includes(q);
      });
    }
    const byDep = {};
    data.forEach(r => {
      const dep = r.departamento || "Sin departamento";
      (byDep[dep] = byDep[dep] || []).push(r);
    });
    const sumK = (arr, k) => arr.reduce((a, r) => a + (parseFloat(r[k]) || 0), 0);
    const r2 = (n) => Math.round(n * 100) / 100;
    const subtotalOf = (arr) => {
      if (tipo === "diario") {
        const tard = arr.filter(r => (parseFloat(r.minutos_tarde) || 0) > 0).length;
        return { horas_laboradas: r2(sumK(arr, "horas_laboradas")), minutos_tarde: Math.round(sumK(arr, "minutos_tarde")), estado: `${tard} tard.` };
      }
      if (tipo === "resumen") {
        return { dias_trabajados: Math.round(sumK(arr, "dias_trabajados")), horas_laboradas: r2(sumK(arr, "horas_laboradas")), tardanzas: Math.round(sumK(arr, "tardanzas")), min_tarde_total: Math.round(sumK(arr, "min_tarde_total")), incompletas: Math.round(sumK(arr, "incompletas")) };
      }
      return { dias_laborables: Math.round(sumK(arr, "dias_laborables")), dias_presente: Math.round(sumK(arr, "dias_presente")), dias_ausente: Math.round(sumK(arr, "dias_ausente")) };
    };
    const out = [];
    Object.keys(byDep).sort((a, b) => a.localeCompare(b)).forEach(dep => {
      const items = byDep[dep];
      out.push({ _type: "dep", dep, count: items.length });
      if (tipo === "diario") {
        const byDay = {};
        items.forEach(r => { const f = r.fecha || "—"; (byDay[f] = byDay[f] || []).push(r); });
        Object.keys(byDay).sort().forEach(day => {
          out.push({ _type: "day", day });
          byDay[day].sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || "")))
            .forEach(r => out.push({ _type: "row", r }));
        });
      } else {
        items.sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || "")))
          .forEach(r => out.push({ _type: "row", r }));
      }
      out.push({ _type: "subtotal", dep, vals: subtotalOf(items) });
    });
    if (data.length) out.push({ _type: "total", vals: subtotalOf(data) });
    return out;
  }, [rows, search, searchField, tipo, selDeps]);

  const visibleCount = displayRows.filter(x => x._type === "row").length;

  return (
    <Card>
      <CardHeader><CardTitle className="text-lg">Reportes de asistencia</CardTitle></CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <Label className="text-xs">Reporte</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="w-44" data-testid="reporte-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="diario">Entradas/Salidas diarias</SelectItem>
                <SelectItem value="resumen">Resumen por empleado</SelectItem>
                <SelectItem value="ausentismo">Ausentismo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Desde</Label>
            <Input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="w-40" data-testid="reporte-desde" /></div>
          <div><Label className="text-xs">Hasta</Label>
            <Input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="w-40" data-testid="reporte-hasta" /></div>
          <div>
            <Label className="text-xs">Departamentos</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-56 justify-between font-normal" data-testid="reporte-dep-filter">
                  <span className="truncate flex items-center gap-1">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    {selDeps.length === 0 ? "Todos los departamentos" : `${selDeps.length} seleccionado(s)`}
                  </span>
                  <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="start">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="text-xs text-slate-500">{selDeps.length} de {depList.length}</span>
                  <button className="text-xs text-indigo-600 hover:underline" onClick={() => setSelDeps([])} data-testid="reporte-dep-clear">Limpiar</button>
                </div>
                <div className="max-h-64 overflow-auto p-2 space-y-0.5">
                  {depList.length === 0 ? (
                    <p className="text-xs text-slate-400 px-1 py-2">Sin departamentos</p>
                  ) : depList.map(d => (
                    <label key={d} className="flex items-center gap-2 px-1 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer text-sm">
                      <Checkbox
                        checked={selDeps.includes(d)}
                        onCheckedChange={(ck) => setSelDeps(prev => ck ? [...prev, d] : prev.filter(x => x !== d))}
                        data-testid={`reporte-dep-opt-${d}`}
                      />
                      <span className="truncate">{d}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Button onClick={cargar} disabled={loading} data-testid="reporte-buscar">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Buscar
          </Button>
          <Button variant="outline" onClick={exportar} data-testid="reporte-export">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>

        {/* Filtro en vivo con tipo de búsqueda */}
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <Label className="text-xs">Buscar por</Label>
            <Select value={searchField} onValueChange={setSearchField}>
              <SelectTrigger className="w-44" data-testid="reporte-search-field"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="departamento">Departamento</SelectItem>
                <SelectItem value="nombre">Empleado</SelectItem>
                <SelectItem value="numero">No. Empleado</SelectItem>
                <SelectItem value="estado">Estado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Filtro</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Escribe para filtrar en vivo…"
                className="pl-8" data-testid="reporte-search" />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="reporte-search-clear">
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {totales && (
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            <Badge variant="secondary" data-testid="reporte-total-visibles">Mostrando: {visibleCount}</Badge>
            <Badge variant="secondary">Registros: {totales.registros}</Badge>
            <Badge variant="secondary">Horas: {totales.horas_laboradas}</Badge>
            <Badge variant="secondary">Tardanzas: {totales.tardanzas}</Badge>
            <Badge variant="secondary">Incompletas: {totales.incompletas}</Badge>
          </div>
        )}

        <div className="overflow-auto border rounded-lg max-h-[60vh]">
          <table className="w-full text-sm" data-testid="reporte-tabla">
            <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-10">
              <tr>{cols.map(c => <th key={c[0]} className={`px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 ${c[0] === "nombre" ? "max-lg:sticky max-lg:left-0 max-lg:z-20" : ""}`}>{c[1]}</th>)}</tr>
            </thead>
            <tbody>
              {displayRows.length === 0 ? (
                <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-slate-400" data-testid="reporte-sin-datos">
                  {rows.length === 0 ? "Sin datos para el rango seleccionado" : "Sin resultados para el filtro"}
                </td></tr>
              ) : displayRows.map((x, i) => {
                if (x._type === "dep") return (
                  <tr key={`dep-${i}`} className="bg-indigo-50 dark:bg-indigo-900/30" data-testid="reporte-grupo-departamento">
                    <td colSpan={cols.length} className="px-3 py-2 font-semibold text-indigo-800 dark:text-indigo-200">
                      <Building2 className="w-4 h-4 inline mr-1 -mt-0.5" />{x.dep}
                      <span className="font-normal text-xs text-slate-500 ml-2">({x.count})</span>
                    </td>
                  </tr>
                );
                if (x._type === "day") return (
                  <tr key={`day-${i}`} className="bg-slate-50 dark:bg-slate-800/40">
                    <td colSpan={cols.length} className="px-3 py-1 pl-8 text-xs font-medium text-slate-500 dark:text-slate-400" data-testid="reporte-grupo-dia">{x.day}</td>
                  </tr>
                );
                if (x._type === "subtotal") return (
                  <tr key={`sub-${i}`} className="bg-indigo-100/70 dark:bg-indigo-900/40 border-t dark:border-slate-700" data-testid="reporte-subtotal">
                    {cols.map(c => (
                      <td key={c[0]} className={`px-3 py-1.5 text-xs font-semibold text-indigo-900 dark:text-indigo-100 ${c[0] === "nombre" ? "max-lg:sticky max-lg:left-0 max-lg:z-[1] max-lg:bg-indigo-100 dark:max-lg:bg-indigo-900" : ""}`}>
                        {c[0] === "nombre" ? `Subtotal ${x.dep}` : c[0] === "minutos_tarde" ? minTardeStr(x.vals[c[0]]) : (x.vals[c[0]] ?? "")}
                      </td>
                    ))}
                  </tr>
                );
                if (x._type === "total") return (
                  <tr key={`tot-${i}`} className="bg-indigo-600 text-white" data-testid="reporte-total-general">
                    {cols.map(c => (
                      <td key={c[0]} className={`px-3 py-2 text-xs font-bold ${c[0] === "nombre" ? "max-lg:sticky max-lg:left-0 max-lg:z-[1] max-lg:bg-indigo-600" : ""}`}>
                        {c[0] === "nombre" ? "TOTAL GENERAL" : c[0] === "minutos_tarde" ? minTardeStr(x.vals[c[0]]) : (x.vals[c[0]] ?? "")}
                      </td>
                    ))}
                  </tr>
                );
                const r = x.r;
                return (
                  <tr key={`row-${i}`} className="border-t dark:border-slate-700">
                    {cols.map(c => (
                      <td key={c[0]} className={`px-3 py-2 ${c[0] === "nombre" ? "max-lg:sticky max-lg:left-0 max-lg:z-[1] max-lg:bg-white dark:max-lg:bg-slate-900" : ""}`}>
                        {c[0] === "estado" ? <EstadoBadge v={r[c[0]]} />
                          : c[0] === "minutos_tarde" ? minTardeStr(r[c[0]])
                          : c[0] === "observaciones" ? <ObservacionCell r={r} getAuthHeaders={getAuthHeaders} />
                          : (r[c[0]] ?? "—")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function EstadoBadge({ v }) {
  const map = { Normal: "bg-green-100 text-green-700", Tarde: "bg-amber-100 text-amber-700", Incompleto: "bg-red-100 text-red-700" };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[v] || "bg-slate-100 text-slate-600"}`}>{v}</span>;
}

function ObservacionCell({ r, getAuthHeaders }) {
  const [val, setVal] = useState(r.observaciones || "");
  const [savedVal, setSavedVal] = useState(r.observaciones || "");
  const [saving, setSaving] = useState(false);
  const dirty = val !== savedVal;
  const guardar = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/observaciones`,
        { numero_empleado: r.numero_empleado, fecha: r.fecha, observacion: val }, getAuthHeaders());
      r.observaciones = val;
      setSavedVal(val);
      toast.success("Observación guardada");
    } catch (e) {
      toast.error("Error al guardar la observación");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-center gap-1 min-w-[240px]">
      <Input
        value={val}
        onChange={e => setVal(e.target.value)}
        placeholder="Escribir observación..."
        className="h-7 text-xs"
        data-testid={`obs-input-${r.numero_empleado}-${r.fecha}`}
      />
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 shrink-0"
        onClick={guardar}
        disabled={saving || !dirty}
        title="Guardar observación"
        data-testid={`obs-save-${r.numero_empleado}-${r.fecha}`}
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <CheckCircle2 className={`w-3.5 h-3.5 ${dirty ? "text-indigo-600" : "text-slate-300"}`} />}
      </Button>
    </div>
  );
}

/* ============ IMPORTAR ============ */
function ImportarTab({ getAuthHeaders, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapeo, setMapeo] = useState({});
  const [loading, setLoading] = useState(false);
  const [hist, setHist] = useState([]);

  const loadHist = useCallback(async () => {
    try { const r = await axios.get(`${API}/importaciones`, getAuthHeaders()); setHist(r.data.items || []); } catch (e) {}
  }, [getAuthHeaders]);
  useEffect(() => { loadHist(); }, [loadHist]);

  const doPreview = async () => {
    if (!file) return toast.error("Selecciona un archivo");
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/importar/preview`, fd, getAuthHeaders());
      setPreview(r.data); setMapeo(r.data.mapeo_sugerido || {});
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo leer el archivo"); }
    finally { setLoading(false); }
  };

  const doImport = async () => {
    if (!file) return toast.error("Selecciona un archivo");
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (mapeo.numero) fd.append("col_numero", mapeo.numero);
      if (mapeo.datetime) fd.append("col_datetime", mapeo.datetime);
      if (mapeo.fecha) fd.append("col_fecha", mapeo.fecha);
      if (mapeo.hora) fd.append("col_hora", mapeo.hora);
      if (mapeo.nombre) fd.append("col_nombre", mapeo.nombre);
      if (mapeo.estado) fd.append("col_estado", mapeo.estado);
      const r = await axios.post(`${API}/importar`, fd, getAuthHeaders());
      const d = r.data;
      toast.success(`Importadas ${d.insertadas} · Duplicadas ${d.duplicadas} · Nuevos empleados ${d.nuevos_empleados}`);
      setPreview(null); setFile(null); loadHist(); onImported && onImported();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al importar"); }
    finally { setLoading(false); }
  };

  const colOptions = preview?.columnas || [];

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card>
        <CardHeader><CardTitle className="text-lg">Importar marcaciones</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Exporta desde tu ponchador (Excel, CSV o TXT) y súbelo aquí. Se detectan las columnas automáticamente y se evitan duplicados.
          </p>
          <Input type="file" accept=".xlsx,.xls,.csv,.txt" onChange={e => { setFile(e.target.files[0]); setPreview(null); }} data-testid="import-file" />
          <div className="flex gap-2">
            <Button variant="outline" onClick={doPreview} disabled={loading || !file} data-testid="import-preview-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Previsualizar
            </Button>
            <Button onClick={doImport} disabled={loading || !file} data-testid="import-btn">
              Importar
            </Button>
          </div>

          {preview && (
            <div className="mt-2 border rounded-lg p-3 bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 mb-2">{preview.total_filas} filas detectadas. Ajusta el mapeo si es necesario:</p>
              <div className="grid grid-cols-2 gap-2">
                {[["numero", "No. Empleado *"], ["datetime", "Fecha y Hora"], ["fecha", "Fecha"], ["hora", "Hora"], ["nombre", "Nombre"], ["estado", "Entrada/Salida"], ["departamento", "Departamento"]].map(([k, lbl]) => (
                  <div key={k}>
                    <Label className="text-xs">{lbl}</Label>
                    <Select value={mapeo[k] || "__none__"} onValueChange={v => setMapeo(m => ({ ...m, [k]: v === "__none__" ? null : v }))}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`map-${k}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">—</SelectItem>
                        {colOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Historial de importaciones</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-auto border rounded-lg max-h-[60vh]">
            <table className="w-full text-sm" data-testid="import-hist">
              <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-10"><tr>
                <th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Archivo</th><th className="px-3 py-2 bg-slate-100 dark:bg-slate-800">Insert.</th>
                <th className="px-3 py-2 bg-slate-100 dark:bg-slate-800">Dup.</th><th className="px-3 py-2 bg-slate-100 dark:bg-slate-800">Fecha</th>
              </tr></thead>
              <tbody>
                {hist.length === 0 ? <tr><td colSpan={4} className="px-3 py-4 text-center text-slate-400">Sin importaciones</td></tr>
                  : hist.map(h => (
                    <tr key={h.id} className="border-t dark:border-slate-700">
                      <td className="px-3 py-2 truncate max-w-[160px]">{h.filename}</td>
                      <td className="px-3 py-2 text-center">{h.insertadas}</td>
                      <td className="px-3 py-2 text-center">{h.duplicadas}</td>
                      <td className="px-3 py-2 text-center text-xs">{new Date(h.fecha).toLocaleString("es-DO")}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ============ EMPLEADOS ============ */
function EmpleadosTab({ getAuthHeaders }) {
  const [items, setItems] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [deptos, setDeptos] = useState([]);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try {
      const [e, h, d, u] = await Promise.all([
        axios.get(`${API}/empleados`, getAuthHeaders()),
        axios.get(`${API}/horarios`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders()),
        axios.get(`${API}/usuarios-plataforma`, getAuthHeaders()),
      ]);
      setItems(e.data.items || []); setHorarios(h.data.items || []);
      setDeptos(d.data.items || []); setUsers(u.data.items || []);
    } catch (er) { toast.error("Error al cargar empleados"); }
  }, [getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await axios.put(`${API}/empleados/${edit.id}`, {
        nombre: edit.nombre, departamento: edit.departamento,
        horario_id: edit.horario_id || null, activo: edit.activo,
        supervisor_user_id: edit.supervisor_user_id || "",
        supervisor_nombre: edit.supervisor_nombre || "",
        supervisor_email: edit.supervisor_email || "",
        gerencia: !!edit.gerencia,
        licencia_activa: !!edit.licencia_activa,
        licencia_desde: edit.licencia_desde || "",
        licencia_hasta: edit.licencia_hasta || "",
        maternidad_activa: !!edit.maternidad_activa,
        maternidad_hora_salida: edit.maternidad_hora_salida || "",
      }, getAuthHeaders());
      toast.success("Empleado actualizado"); setEdit(null); load();
    } catch (e) { toast.error("Error al guardar"); }
  };

  const [importing, setImporting] = useState(false);
  const importDatos = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await axios.post(`${API}/empleados/importar-datos`, fd, getAuthHeaders());
      toast.success(`Departamentos cargados · ${r.data.actualizados} actualizados · ${r.data.creados} nuevos`);
      load();
    } catch (er) { toast.error(er.response?.data?.detail || "Error al importar"); }
    finally { setImporting(false); }
  };

  const del = async (e) => {
    if (!window.confirm(`¿Eliminar a "${e.nombre}"? Sus marcaciones históricas se conservan; solo se quita de la lista de empleados.`)) return;
    try {
      await axios.delete(`${API}/empleados/${e.id}`, getAuthHeaders());
      toast.success("Empleado eliminado"); load();
    } catch (er) { toast.error(er.response?.data?.detail || "No se pudo eliminar"); }
  };

  const toggleActivo = async (e) => {
    const activo = e.activo !== false;
    try {
      await axios.put(`${API}/empleados/${e.id}`, { activo: !activo }, getAuthHeaders());
      toast.success(activo ? "Empleado desactivado (oculto en reportes)" : "Empleado activado");
      load();
    } catch (er) { toast.error("No se pudo actualizar el estado"); }
  };

  const grupos = useMemo(() => {
    const isActive = (e) => e.activo !== false;
    const byName = (a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""));
    const sinDep = (e) => {
      const d = String(e.departamento || "").trim();
      return !d || d.toLowerCase() === "sin departamento";
    };

    // 1) Activos CON departamento, agrupados y ordenados alfabéticamente
    const by = {};
    const activosSinDep = [];
    items.filter(isActive).forEach(e => {
      if (sinDep(e)) { activosSinDep.push(e); return; }
      const d = e.departamento;
      (by[d] = by[d] || []).push(e);
    });
    const out = Object.keys(by).sort((a, b) => a.localeCompare(b)).map(dep => ({
      dep, emps: by[dep].sort(byName),
    }));

    // 2) Activos SIN departamento (debajo de todos los departamentos)
    if (activosSinDep.length) out.push({ dep: "Sin departamento", emps: activosSinDep.sort(byName) });

    // 3) Inactivos SIEMPRE al final
    const inactivos = items.filter(e => !isActive(e)).sort(byName);
    if (inactivos.length) out.push({ dep: "Inactivos", emps: inactivos });

    return out;
  }, [items]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-lg">Empleados ({items.length})</CardTitle>
        <div>
          <input id="emp-import-file" type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden" onChange={importDatos} data-testid="emp-import-input" />
          <Button size="sm" variant="outline" disabled={importing}
            onClick={() => document.getElementById("emp-import-file").click()} data-testid="emp-import-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Importar departamentos (Excel ponchador)
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-auto border rounded-lg max-h-[60vh]">
          <table className="w-full text-sm" data-testid="empleados-tabla">
            <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-10"><tr>
              <th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">No.</th><th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Nombre</th>
              <th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Departamento</th><th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Supervisor</th><th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Horario</th>
              <th className="px-3 py-2 bg-slate-100 dark:bg-slate-800">Activo</th><th className="px-3 py-2 bg-slate-100 dark:bg-slate-800"></th>
            </tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Aún no hay empleados. Importa marcaciones para crearlos.</td></tr>
                : grupos.flatMap(g => ([
                  <tr key={`dep-${g.dep}`} className="bg-indigo-50 dark:bg-indigo-900/30" data-testid="emp-grupo-departamento">
                    <td colSpan={7} className="px-3 py-2 font-semibold text-indigo-800 dark:text-indigo-200">
                      <Building2 className="w-4 h-4 inline mr-1 -mt-0.5" />{g.dep}
                      <span className="font-normal text-xs text-slate-500 ml-2">({g.emps.length})</span>
                    </td>
                  </tr>,
                  ...g.emps.map(e => {
                    const activo = e.activo !== false;
                    return (
                    <tr key={e.id} className={`border-t dark:border-slate-700 ${activo ? "" : "opacity-50 bg-slate-50 dark:bg-slate-800/30"}`}>
                      <td className="px-3 py-2 font-mono">{e.numero_empleado}</td>
                      <td className="px-3 py-2">
                        {e.nombre}
                        {!activo && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-500">INACTIVO</span>}
                        {e.licencia_activa && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300" title={`Licencia: ${e.licencia_desde || "?"} → ${e.licencia_hasta || "?"} (sin alertas en ese rango)`} data-testid={`emp-badge-licencia-${e.numero_empleado}`}>LICENCIA</span>}
                        {e.maternidad_activa && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300" title={`Permiso maternidad: sale a las ${e.maternidad_hora_salida || "?"}`} data-testid={`emp-badge-maternidad-${e.numero_empleado}`}>MATERNIDAD</span>}
                      </td>
                      <td className="px-3 py-2">{e.departamento || "—"}</td>
                      <td className="px-3 py-2">{e.supervisor_nombre || "—"}{e.supervisor_origen === "usuarios" && <span className="block text-[10px] text-slate-400">sincronizado con Usuarios</span>}</td>
                      <td className="px-3 py-2">{horarios.find(h => h.id === e.horario_id)?.nombre || "Por defecto"}</td>
                      <td className="px-3 py-2 text-center">{activo ? "✓" : "✗"}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" title={activo ? "Desactivar" : "Activar"} onClick={() => toggleActivo(e)} data-testid={`emp-toggle-${e.numero_empleado}`}>
                          {activo ? <XCircle className="w-4 h-4 text-amber-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEdit({ ...e })} data-testid={`emp-edit-${e.numero_empleado}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => del(e)} data-testid={`emp-del-${e.numero_empleado}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                    );
                  })
                ]))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={!!edit} onOpenChange={o => !o && setEdit(null)}>
        <DialogContent data-testid="emp-edit-dialog">
          <DialogHeader><DialogTitle>Editar empleado</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>No. Empleado</Label><Input value={edit.numero_empleado} disabled /></div>
              <div><Label>Nombre</Label><Input value={edit.nombre} onChange={e => setEdit({ ...edit, nombre: e.target.value })} data-testid="emp-nombre" /></div>
              <div><Label>Departamento</Label>
                <Select value={edit.departamento || "__none__"} onValueChange={v => setEdit({ ...edit, departamento: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="emp-depto"><SelectValue placeholder="Sin departamento" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin departamento</SelectItem>
                    {deptos.map(d => <SelectItem key={d.id} value={d.nombre}>{d.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Horario</Label>
                <Select value={edit.horario_id || "__def__"} onValueChange={v => setEdit({ ...edit, horario_id: v === "__def__" ? null : v })}>
                  <SelectTrigger data-testid="emp-horario"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__def__">Por defecto</SelectItem>
                    {horarios.map(h => <SelectItem key={h.id} value={h.id}>{h.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Supervisor</Label>
                {edit.supervisor_origen === "usuarios" ? (
                  <div>
                    <div className="text-sm p-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800" data-testid="emp-supervisor-sync">
                      {edit.supervisor_nombre || "Sin supervisor"}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Sincronizado con el submenú <strong>Usuarios</strong> (usuario: {edit.usuario_nombre}). Para cambiarlo, edita el supervisor del usuario en Configuración → Usuarios.</p>
                  </div>
                ) : (
                <Select value={edit.supervisor_user_id || "__none__"} onValueChange={v => {
                  if (v === "__none__") setEdit({ ...edit, supervisor_user_id: "", supervisor_nombre: "", supervisor_email: "" });
                  else { const u = users.find(x => x.id === v); setEdit({ ...edit, supervisor_user_id: v, supervisor_nombre: u?.nombre || "", supervisor_email: u?.email || "" }); }
                }}>
                  <SelectTrigger data-testid="emp-supervisor"><SelectValue placeholder="Sin supervisor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin supervisor</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.nombre}{u.email ? ` (${u.email})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                )}
              </div>
              <label className="flex items-start gap-2 rounded-md border border-slate-200 dark:border-slate-700 p-3 cursor-pointer" htmlFor="emp-gerencia">
                <input type="checkbox" id="emp-gerencia" className="mt-0.5 w-4 h-4 accent-indigo-600"
                  checked={!!edit.gerencia} onChange={ev => setEdit({ ...edit, gerencia: ev.target.checked })}
                  data-testid="emp-gerencia" />
                <span className="text-xs text-slate-700 dark:text-slate-300">
                  <span className="font-medium">Gerencia</span>
                  <span className="block text-[11px] text-slate-500">Si está activo, este empleado queda exento de la alerta de <em>salida temprana</em> (tolerancia gerencial).</span>
                </span>
              </label>
              <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer" htmlFor="emp-licencia">
                  <input type="checkbox" id="emp-licencia" className="mt-0.5 w-4 h-4 accent-amber-600"
                    checked={!!edit.licencia_activa} onChange={ev => setEdit({ ...edit, licencia_activa: ev.target.checked })}
                    data-testid="emp-licencia" />
                  <span className="text-xs text-slate-700 dark:text-slate-300">
                    <span className="font-medium">Licencia</span>
                    <span className="block text-[11px] text-slate-500">Durante el rango de fechas NO se enviará ninguna alerta (tardanza, no ponchó, ausencia, marcación incompleta ni salida temprana).</span>
                  </span>
                </label>
                {edit.licencia_activa && (
                  <div className="grid grid-cols-2 gap-2 pl-6">
                    <div><Label className="text-[11px]">Desde</Label>
                      <Input type="date" value={edit.licencia_desde || ""} onChange={ev => setEdit({ ...edit, licencia_desde: ev.target.value })} data-testid="emp-licencia-desde" /></div>
                    <div><Label className="text-[11px]">Hasta</Label>
                      <Input type="date" value={edit.licencia_hasta || ""} onChange={ev => setEdit({ ...edit, licencia_hasta: ev.target.value })} data-testid="emp-licencia-hasta" /></div>
                  </div>
                )}
              </div>
              <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer" htmlFor="emp-maternidad">
                  <input type="checkbox" id="emp-maternidad" className="mt-0.5 w-4 h-4 accent-pink-600"
                    checked={!!edit.maternidad_activa} onChange={ev => setEdit({ ...edit, maternidad_activa: ev.target.checked })}
                    data-testid="emp-maternidad" />
                  <span className="text-xs text-slate-700 dark:text-slate-300">
                    <span className="font-medium">Permiso Maternidad</span>
                    <span className="block text-[11px] text-slate-500">La hora indicada pasa a ser su hora final de horario: si sale a esa hora o después, no se envía alerta de salida temprana.</span>
                  </span>
                </label>
                {edit.maternidad_activa && (
                  <div className="pl-6 w-40"><Label className="text-[11px]">Hora de salida</Label>
                    <Input type="time" value={edit.maternidad_hora_salida || ""} onChange={ev => setEdit({ ...edit, maternidad_hora_salida: ev.target.value })} data-testid="emp-maternidad-hora" /></div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancelar</Button>
            <Button onClick={save} data-testid="emp-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ============ CONEXIÓN / SINCRONIZACIÓN AUTOMÁTICA ============ */
function ConexionTab({ getAuthHeaders }) {
  const [disp, setDisp] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [form, setForm] = useState(null);
  const [savingInt, setSavingInt] = useState(false);
  const [intervalMin, setIntervalMin] = useState(10);
  const [corteES, setCorteES] = useState("12:00");
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [e, c] = await Promise.all([
        axios.get(`${API}/sync/estado`, getAuthHeaders()),
        axios.get(`${API}/config`, getAuthHeaders()),
      ]);
      setDisp(e.data.dispositivos || []);
      setCfg(c.data);
      setIntervalMin(c.data.sync_interval_min || 10);
      setCorteES(c.data.corte_entrada_salida || "12:00");
    } catch (er) { toast.error("Error al cargar la configuración"); }
  }, [getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const saveDevice = async () => {
    if (!form.nombre.trim() || !form.ip.trim()) return toast.error("Nombre e IP son obligatorios");
    try {
      const body = { nombre: form.nombre, ip: form.ip, puerto: parseInt(form.puerto) || 4370, activo: form.activo !== false };
      if (form.id) await axios.put(`${API}/dispositivos/${form.id}`, body, getAuthHeaders());
      else await axios.post(`${API}/dispositivos`, body, getAuthHeaders());
      toast.success("Ponchador guardado"); setForm(null); load();
    } catch (e) { toast.error("Error al guardar"); }
  };

  const delDevice = async (id) => {
    if (!window.confirm("¿Eliminar este ponchador?")) return;
    try { await axios.delete(`${API}/dispositivos/${id}`, getAuthHeaders()); toast.success("Eliminado"); load(); }
    catch (e) { toast.error("Error al eliminar"); }
  };

  const saveInterval = async () => {
    setSavingInt(true);
    try { await axios.put(`${API}/config`, { sync_interval_min: parseInt(intervalMin) || 10, corte_entrada_salida: corteES || "12:00" }, getAuthHeaders()); toast.success("Configuración actualizada"); }
    catch (e) { toast.error("Error"); } finally { setSavingInt(false); }
  };

  const regenKey = async () => {
    if (!window.confirm("Al regenerar la llave, el agente actual dejará de funcionar hasta que descargues el nuevo. ¿Continuar?")) return;
    try { const r = await axios.post(`${API}/config/regenerate-key`, {}, getAuthHeaders()); setCfg({ ...cfg, api_key: r.data.api_key }); toast.success("Llave regenerada. Descarga el agente nuevamente."); }
    catch (e) { toast.error("Error"); }
  };

  const downloadAgent = async () => {
    try {
      const r = await axios.get(`${API}/sync/agent-script`, { ...getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "qasesores_ponche_agent.py"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Agente descargado");
    } catch (e) { toast.error("Error al descargar el agente"); }
  };

  const downloadPackage = async () => {
    try {
      const r = await axios.get(`${API}/sync/agent-package`, { ...getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "QAsesoresPoncheAgente.zip"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Paquete descargado");
    } catch (e) { toast.error("Error al descargar el paquete"); }
  };

  const copyKey = () => { navigator.clipboard.writeText(cfg?.api_key || ""); toast.success("Llave copiada"); };

  const solicitarSync = async () => {
    setSyncing(true);
    try { await axios.post(`${API}/sync/solicitar`, {}, getAuthHeaders()); toast.success("Sincronización solicitada. El agente la ejecutará en los próximos segundos."); }
    catch (e) { toast.error("Error al solicitar la sincronización"); } finally { setSyncing(false); }
  };

  const fmtFecha = (f) => f ? new Date(f).toLocaleString("es-DO") : "Nunca";

  return (
    <div className="space-y-4">
      {/* Ponchadores */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2"><Server className="w-5 h-5" /> Ponchadores conectados</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={solicitarSync} disabled={syncing} data-testid="sync-ahora">
              {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sincronizar ahora
            </Button>
            <Button size="sm" onClick={() => setForm({ nombre: "", ip: "", puerto: 4370, activo: true })} data-testid="disp-nuevo">
              <Plus className="w-4 h-4 mr-1" /> Agregar ponchador
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {disp.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">Aún no has agregado ponchadores. Agrega la IP de cada U160 de tu red.</p>
          ) : (
            <div className="space-y-2">
              {disp.map(d => (
                <div key={d.id} className="flex items-center justify-between border rounded-lg p-3 dark:border-slate-700" data-testid={`disp-${d.id}`}>
                  <div className="flex items-center gap-3">
                    <Wifi className={`w-5 h-5 ${d.ultima_sync && !d.ultimo_error ? "text-green-500" : d.ultimo_error ? "text-red-500" : "text-slate-400"}`} />
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{d.nombre} <span className="text-xs text-slate-400 font-mono">{d.ip}:{d.puerto}</span></p>
                      <p className="text-xs text-slate-500">
                        Última sync: {fmtFecha(d.ultima_sync)}
                        {d.ultimo_error && <span className="text-red-500 ml-2">· Error: {d.ultimo_error.slice(0, 60)}</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setForm({ ...d })}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => delDevice(d.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Agente */}
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Download className="w-5 h-5" /> Agente de Sincronización</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-slate-500 dark:text-slate-400">
              La nube no puede entrar a tu red local por seguridad. Instala este pequeño agente en una PC de la oficina (Windows, siempre encendida) y él enviará los ponches automáticamente.
            </p>
            <ol className="list-decimal list-inside text-slate-600 dark:text-slate-300 space-y-1 text-xs bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3">
              <li>Instala Python 3.9+ (marca "Add to PATH").</li>
              <li>En CMD ejecuta: <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">pip install pyzk requests</code></li>
              <li>Ejecuta: <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">python qasesores_ponche_agent.py</code></li>
              <li>Deja la ventana abierta. ¡Listo, sincroniza solo!</li>
            </ol>
            <Button onClick={downloadAgent} data-testid="download-agent" variant="outline"><Download className="w-4 h-4 mr-1" /> Solo script (.py)</Button>
            <Button onClick={downloadPackage} data-testid="download-package"><Download className="w-4 h-4 mr-1" /> Descargar paquete instalable</Button>
          </CardContent>
        </Card>

        {/* Config */}
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><KeyRound className="w-5 h-5" /> Configuración</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <Label className="text-xs">Frecuencia de sincronización (minutos)</Label>
              <div className="flex gap-2 mt-1">
                <Input type="number" min="1" value={intervalMin} onChange={e => setIntervalMin(e.target.value)} className="w-28" data-testid="sync-interval" />
                <Button variant="outline" onClick={saveInterval} disabled={savingInt} data-testid="sync-interval-save">
                  {savingInt ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Corte Entrada/Salida (marcas hasta esta hora cuentan como Entrada; después, como Salida)</Label>
              <div className="flex gap-2 mt-1">
                <Input type="time" value={corteES} onChange={e => setCorteES(e.target.value)} className="w-32" data-testid="corte-entrada-salida" />
                <Button variant="outline" onClick={saveInterval} disabled={savingInt} data-testid="corte-entrada-salida-save">
                  {savingInt ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Llave de conexión (API key)</Label>
              <div className="flex gap-2 mt-1 items-center">
                <Input readOnly type={showKey ? "text" : "password"} value={cfg?.api_key || ""} className="font-mono text-xs" data-testid="api-key" />
                <Button variant="ghost" size="icon" onClick={() => setShowKey(s => !s)}>{showKey ? <XCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}</Button>
                <Button variant="ghost" size="icon" onClick={copyKey}><Copy className="w-4 h-4" /></Button>
              </div>
              <Button variant="outline" size="sm" className="mt-2" onClick={regenKey} data-testid="regen-key">
                <RefreshCw className="w-3 h-3 mr-1" /> Regenerar llave
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!form} onOpenChange={o => !o && setForm(null)}>
        <DialogContent data-testid="disp-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Editar" : "Agregar"} ponchador</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej: Recepción" data-testid="disp-nombre" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Dirección IP</Label><Input value={form.ip} onChange={e => setForm({ ...form, ip: e.target.value })} placeholder="192.168.1.50" data-testid="disp-ip" /></div>
                <div><Label>Puerto</Label><Input type="number" value={form.puerto} onChange={e => setForm({ ...form, puerto: e.target.value })} data-testid="disp-puerto" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.activo !== false} onChange={e => setForm({ ...form, activo: e.target.checked })} /> Activo
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
            <Button onClick={saveDevice} data-testid="disp-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ DEPARTAMENTOS ============ */
function DepartamentosTab({ getAuthHeaders }) {
  const [items, setItems] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/departamentos`, getAuthHeaders()); setItems(r.data.items || []); }
    catch (e) { toast.error("Error al cargar departamentos"); }
  }, [getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const crear = async () => {
    if (!nuevo.trim()) return;
    try { await axios.post(`${API}/departamentos`, { nombre: nuevo.trim() }, getAuthHeaders()); setNuevo(""); load(); toast.success("Departamento creado"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error al crear"); }
  };
  const guardar = async () => {
    try { await axios.put(`${API}/departamentos/${editItem.id}`, { nombre: editItem.nombre }, getAuthHeaders()); setEditItem(null); load(); toast.success("Actualizado"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };
  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este departamento? Los empleados asignados quedarán sin departamento.")) return;
    try { await axios.delete(`${API}/departamentos/${id}`, getAuthHeaders()); load(); toast.success("Eliminado"); }
    catch (e) { toast.error("Error al eliminar"); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Building2 className="w-5 h-5" /> Departamentos</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input value={nuevo} onChange={e => setNuevo(e.target.value)} placeholder="Nuevo departamento" onKeyDown={e => e.key === "Enter" && crear()} data-testid="dep-nuevo-input" />
          <Button onClick={crear} data-testid="dep-crear"><Plus className="w-4 h-4 mr-1" /> Agregar</Button>
        </div>
        <p className="text-xs text-slate-400">Los departamentos se crean automáticamente al importar o sincronizar los ponches (si el archivo/equipo los trae). También puedes agregarlos aquí y asignarlos a cada empleado.</p>
        <div className="space-y-2">
          {items.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">Aún no hay departamentos.</p>
            : items.map(d => (
              <div key={d.id} className="flex items-center justify-between border rounded-lg p-3 dark:border-slate-700" data-testid={`dep-${d.id}`}>
                {editItem?.id === d.id ? (
                  <Input value={editItem.nombre} onChange={e => setEditItem({ ...editItem, nombre: e.target.value })} className="max-w-xs" data-testid="dep-edit-input" />
                ) : (
                  <div><span className="font-medium text-slate-900 dark:text-white">{d.nombre}</span> <Badge variant="secondary" className="ml-2">{d.empleados} empleados</Badge></div>
                )}
                <div className="flex gap-1">
                  {editItem?.id === d.id ? (
                    <>
                      <Button size="sm" onClick={guardar} data-testid="dep-guardar">Guardar</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditItem(null)}>Cancelar</Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setEditItem({ id: d.id, nombre: d.nombre })} data-testid={`dep-edit-${d.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => eliminar(d.id)} data-testid={`dep-del-${d.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ============ ALERTAS ============ */
const EVENTOS_META = [
  { key: "tardanza", label: "Tardanza", desc: "Cuando el empleado marca entrada después de su tolerancia.", corte: false },
  { key: "no_poncho", label: "No marcó entrada", desc: "Cuando no marca entrada pasado el tiempo configurado tras su hora de entrada.", minutos: true },
  { key: "ausencia", label: "Ausencia", desc: "Cuando no hubo ninguna marcación en todo el día.", corte: true, corteDef: "18:00" },
  { key: "incompleto", label: "Marcación incompleta", desc: "Cuando marcó entrada pero no marcó salida al finalizar el día.", corte: true, corteDef: "18:00" },
  { key: "salida_temprana", label: "Salida temprana", desc: "Cuando marca su salida antes de la hora de salida de su horario.", corte: true, corteDef: "18:00" },
];
const EVENTO_LABEL = Object.fromEntries(EVENTOS_META.map(m => [m.key, m.label]));

function AlertasTab({ getAuthHeaders }) {
  const [eventos, setEventos] = useState(null);
  const [excluidosText, setExcluidosText] = useState("");
  const [sinAlertasText, setSinAlertasText] = useState("");
  const [retroWin, setRetroWin] = useState(1);
  const [saving, setSaving] = useState(false);
  const hoyRD = () => new Date(Date.now() - 4 * 3600 * 1000).toISOString().slice(0, 10);
  const [enviadas, setEnviadas] = useState([]);
  const [fechaEnv, setFechaEnv] = useState(hoyRD);
  const [loadingEnv, setLoadingEnv] = useState(false);

  const loadEnviadas = useCallback(async () => {
    setLoadingEnv(true);
    try {
      const r = await axios.get(`${API}/alertas/enviadas?fecha=${fechaEnv}`, getAuthHeaders());
      setEnviadas(r.data.items || []);
    } catch (e) { /* noop */ } finally { setLoadingEnv(false); }
  }, [getAuthHeaders, fechaEnv]);
  useEffect(() => { loadEnviadas(); }, [loadEnviadas]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/alertas/config`, getAuthHeaders());
      setEventos(r.data.eventos || {});
      setExcluidosText((r.data.supervisores_excluidos || []).join("\n"));
      setSinAlertasText((r.data.empleados_sin_alertas || []).join("\n"));
      setRetroWin(r.data.retro_window_min ?? 1);
    }
    catch (e) { toast.error("Error al cargar alertas"); }
  }, [getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const upd = (key, patch) => setEventos(ev => ({ ...ev, [key]: { ...ev[key], ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      for (const k of Object.keys(eventos)) {
        const e = { ...eventos[k] };
        const rh = (e._rh_text !== undefined ? e._rh_text : (e.rh_emails || []).join("\n"));
        e.rh_emails = rh.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
        delete e._rh_text;
        clean[k] = e;
      }
      const excluidos = excluidosText.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
      const sinAlertas = sinAlertasText.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
      await axios.put(`${API}/alertas/config`, { eventos: clean, supervisores_excluidos: excluidos, empleados_sin_alertas: sinAlertas, retro_window_min: Math.max(1, parseInt(retroWin) || 1) }, getAuthHeaders());
      toast.success("Alertas guardadas"); load();
    } catch (e) { toast.error("Error al guardar"); } finally { setSaving(false); }
  };

  const probar = async () => {
    try { const r = await axios.post(`${API}/alertas/probar`, {}, getAuthHeaders()); toast.success(`Prueba enviada a ${r.data.enviados} destinatario(s)`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error al enviar prueba"); }
  };

  if (!eventos) return <Card><CardContent className="p-6"><Loader2 className="w-5 h-5 animate-spin" /></CardContent></Card>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl">
          Activa las alertas que quieras enviar. <strong>Los destinatarios los controla la plantilla de correo</strong> de cada evento (Configuración → Plantillas de Correo): ahí eliges si va al empleado, a su supervisor y/o a Gestión Humana. Se excluye automáticamente a quien esté de vacaciones o permiso aprobado (coincidencia de nombre ≥95%). Las revisiones corren cada minuto.
        </p>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={probar} data-testid="al-probar">Enviar prueba</Button>
          <Button onClick={save} disabled={saving} data-testid="al-guardar">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar cambios"}</Button>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {EVENTOS_META.map(meta => {
          const e = eventos[meta.key] || {};
          return (
            <Card key={meta.key} data-testid={`al-card-${meta.key}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" /> {meta.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p className="text-xs text-slate-400">{meta.desc}</p>
                <label className="flex items-center gap-2 font-medium">
                  <input type="checkbox" checked={!!e.enabled} onChange={ev => upd(meta.key, { enabled: ev.target.checked })} data-testid={`al-${meta.key}-enabled`} />
                  Activar alerta
                </label>
                {meta.corte && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Hora de corte</Label>
                    <Input type="time" value={e.hora_corte || meta.corteDef} onChange={ev => upd(meta.key, { hora_corte: ev.target.value })} className="w-32" data-testid={`al-${meta.key}-hora`} />
                  </div>
                )}
                {meta.minutos && (
                  <div>
                    <Label className="text-xs">Minutos después de la hora de entrada</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input type="number" min="0" step="5" value={e.minutos_despues ?? 60} onChange={ev => upd(meta.key, { minutos_despues: parseInt(ev.target.value) || 0 })} className="w-24" data-testid={`al-${meta.key}-minutos`} />
                      <span className="text-xs text-slate-400">min (según el horario de cada empleado)</span>
                    </div>
                  </div>
                )}
                <div className="border-t pt-2 dark:border-slate-700">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    <strong>Destinatarios:</strong> se configuran en la <strong>plantilla de correo</strong> de este evento
                    (Configuración → Plantillas de Correo). Marca ahí <em>Usuario Afectado</em> (el empleado), <em>Supervisor del Usuario Asignado</em> (su supervisor) y/o <em>Gestión Humana</em>.
                  </p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Ventana anti-retroactivos */}
      <Card data-testid="al-retro-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-sky-500" /> Ventana anti-retroactivos
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-slate-500">
            Si al evaluar una alerta ya pasaron más de estos minutos desde su momento real (llegada de la marcación o hora de corte), el correo <strong>no se envía</strong> (queda suprimido). Evita correos retroactivos tras un redespliegue o caída. El revisor corre cada minuto.
          </p>
          <div className="flex items-center gap-2">
            <Input type="number" min="1" step="1" value={retroWin} onChange={ev => setRetroWin(ev.target.value)} className="w-24" data-testid="al-retro-minutos" />
            <span className="text-xs text-slate-400">minutos · se guarda con "Guardar cambios"</span>
          </div>
        </CardContent>
      </Card>

      {/* Supervisores excluidos de todas las alertas */}
      <Card data-testid="al-excluidos-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-rose-500" /> Supervisores excluidos de alertas
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-slate-500">
            Los correos listados aquí <strong>nunca</strong> recibirán alertas del Sistema de Ponche, sin importar el evento ni la vía (supervisor, Gestión Humana, correos adicionales). Uno por línea.
          </p>
          <textarea
            value={excluidosText}
            onChange={ev => setExcluidosText(ev.target.value)}
            rows={3}
            placeholder="ejemplo@qasesores.com"
            className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="al-excluidos-textarea"
          />
          <p className="text-xs text-slate-400">Se guarda con el botón "Guardar cambios" de arriba.</p>
        </CardContent>
      </Card>

      {/* Empleados sin alertas de ponche */}
      <Card data-testid="al-sin-alertas-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-4 h-4 text-amber-500" /> Empleados sin alertas de ponche
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-xs text-slate-500">
            Para los empleados listados aquí (correo o nombre exacto, uno por línea) <strong>no se envía ningún correo</strong> de tardanza, no ponchó, ausencia, incompleto ni salida temprana — a nadie. Sus marcajes siguen registrándose normal en los reportes mensuales. Útil para personal que trabaja fuera de la oficina.
          </p>
          <textarea
            value={sinAlertasText}
            onChange={ev => setSinAlertasText(ev.target.value)}
            rows={3}
            placeholder={"mdelgado@qasesores.com.do\naadames@qasesores.com.do"}
            className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="al-sin-alertas-textarea"
          />
          <p className="text-xs text-slate-400">Se guarda con el botón "Guardar cambios" de arriba.</p>
        </CardContent>
      </Card>

      {/* Registro de alertas enviadas */}
      <Card data-testid="al-enviadas-panel">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="w-4 h-4 text-indigo-500" /> Alertas enviadas
            </CardTitle>
            <div className="flex items-center gap-2">
              <Input type="date" value={fechaEnv} onChange={ev => setFechaEnv(ev.target.value)} className="w-40 h-8" data-testid="al-enviadas-fecha" />
              <Button variant="outline" size="sm" onClick={loadEnviadas} data-testid="al-enviadas-refresh">
                {loadingEnv ? <Loader2 className="w-4 h-4 animate-spin" /> : "Actualizar"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {enviadas.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center" data-testid="al-enviadas-vacio">
              No se han enviado alertas en esta fecha.
            </p>
          ) : (
            <div className="overflow-auto border rounded-lg max-h-[45vh]">
              <table className="w-full text-sm" data-testid="al-enviadas-tabla">
                <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-10"><tr>
                  <th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Hora</th>
                  <th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Empleado</th>
                  <th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Departamento</th>
                  <th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Alerta</th>
                  <th className="px-3 py-2 text-left bg-slate-100 dark:bg-slate-800">Destinatarios</th>
                </tr></thead>
                <tbody>
                  {enviadas.map((r, i) => (
                    <tr key={i} className="border-t dark:border-slate-700" data-testid={`al-enviada-row-${i}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{r.at ? new Date(new Date(r.at).getTime() - 4 * 3600 * 1000).toISOString().slice(11, 16) : "—"}</td>
                      <td className="px-3 py-2">{r.nombre || r.numero_empleado}</td>
                      <td className="px-3 py-2">{r.departamento || "—"}</td>
                      <td className="px-3 py-2">
                        <Badge variant="secondary">{EVENTO_LABEL[r.evento] || r.evento}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{(r.destinatarios || []).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ============ HORARIOS ============ */
function HorariosTab({ getAuthHeaders }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/horarios`, getAuthHeaders()); setItems(r.data.items || []); } catch (e) {}
  }, [getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const nuevo = () => setForm({ nombre: "", hora_entrada: "08:00", hora_salida: "17:00", almuerzo_minutos: 60, tolerancia_minutos: 10, dias_laborables: [0, 1, 2, 3, 4], es_default: false });

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("Ponle un nombre al horario");
    try {
      if (form.id) await axios.put(`${API}/horarios/${form.id}`, form, getAuthHeaders());
      else await axios.post(`${API}/horarios`, form, getAuthHeaders());
      toast.success("Horario guardado"); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este horario?")) return;
    try { await axios.delete(`${API}/horarios/${id}`, getAuthHeaders()); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };

  const toggleDia = (d) => setForm(f => {
    const has = f.dias_laborables.includes(d);
    return { ...f, dias_laborables: has ? f.dias_laborables.filter(x => x !== d) : [...f.dias_laborables, d] };
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Horarios</CardTitle>
        <Button size="sm" onClick={nuevo} data-testid="horario-nuevo"><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map(h => (
            <div key={h.id} className="flex items-center justify-between border rounded-lg p-3 dark:border-slate-700" data-testid={`horario-${h.id}`}>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">{h.nombre} {h.es_default && <Badge variant="secondary" className="ml-1">Por defecto</Badge>}</p>
                <p className="text-xs text-slate-500">{h.hora_entrada}–{h.hora_salida} · Almuerzo {h.almuerzo_minutos}min · Tolerancia {h.tolerancia_minutos}min</p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setForm({ ...h })}><Edit className="w-4 h-4" /></Button>
                {!h.es_default && <Button size="sm" variant="ghost" onClick={() => del(h.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={!!form} onOpenChange={o => !o && setForm(null)}>
        <DialogContent data-testid="horario-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Editar" : "Nuevo"} horario</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="horario-nombre" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Entrada</Label><Input type="time" value={form.hora_entrada} onChange={e => setForm({ ...form, hora_entrada: e.target.value })} data-testid="horario-entrada" /></div>
                <div><Label>Salida</Label><Input type="time" value={form.hora_salida} onChange={e => setForm({ ...form, hora_salida: e.target.value })} data-testid="horario-salida" /></div>
                <div><Label>Almuerzo (min)</Label><Input type="number" value={form.almuerzo_minutos} onChange={e => setForm({ ...form, almuerzo_minutos: parseInt(e.target.value) || 0 })} /></div>
                <div><Label>Tolerancia (min)</Label><Input type="number" value={form.tolerancia_minutos} onChange={e => setForm({ ...form, tolerancia_minutos: parseInt(e.target.value) || 0 })} /></div>
              </div>
              <div>
                <Label>Días laborables</Label>
                <div className="flex gap-1 mt-1">
                  {DIAS.map(([d, lbl]) => (
                    <button key={d} type="button" onClick={() => toggleDia(parseInt(d))}
                      className={`px-2 py-1 rounded text-xs ${form.dias_laborables.includes(parseInt(d)) ? "bg-indigo-600 text-white" : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.es_default} onChange={e => setForm({ ...form, es_default: e.target.checked })} />
                Usar como horario por defecto
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
            <Button onClick={save} data-testid="horario-guardar">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ============ ENVÍO DE REPORTES (PROGRAMADO) ============ */
const EMPTY_PROG = { tipo: "departamento", departamento: "", destinatario_user_id: "", cc_emails: "", dias: [], hora: "08:00", activo: true };

function EnvioReportesTab({ getAuthHeaders }) {
  const [items, setItems] = useState([]);
  const [deps, setDeps] = useState([]);
  const [sups, setSups] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [sendingMesAnt, setSendingMesAnt] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_PROG);
  const [hist, setHist] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        axios.get(`${API}/reportes-programados`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders()),
        axios.get(`${API}/reportes-programados/supervisores`, getAuthHeaders()),
        axios.get(`${API}/reportes-programados/historial`, getAuthHeaders()),
      ]);
      setItems(r1.data.items || []);
      setDeps(r2.data.items || []);
      setSups(r3.data.items || []);
      setHist(r4.data.items || []);
    } catch (e) { toast.error("Error al cargar configuraciones de envío"); }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm(EMPTY_PROG); setOpen(true); };
  const openEdit = (it) => {
    setEditId(it.id);
    setForm({ tipo: it.tipo || "departamento", departamento: it.departamento || "", destinatario_user_id: it.destinatario_user_id || "", cc_emails: (it.cc_emails || []).join(", "), dias: it.dias || [], hora: it.hora || "08:00", activo: it.activo !== false });
    setOpen(true);
  };

  const toggleDia = (d) => setForm(f => ({ ...f, dias: f.dias.includes(d) ? f.dias.filter(x => x !== d) : [...f.dias, d].sort((a, b) => a - b) }));

  const save = async () => {
    const sup = sups.find(s => s.id === form.destinatario_user_id);
    if (!sup) { toast.error("Seleccione el destinatario"); return; }
    if (form.tipo === "departamento" && !form.departamento) { toast.error("Seleccione el departamento"); return; }
    if (form.dias.length === 0) { toast.error("Seleccione al menos un día del mes"); return; }
    if (form.dias.length === 1 && form.dias[0] !== 1) { toast.error("Si configura un solo día, debe ser el día 1 (envía el mes anterior completo)"); return; }
    const ccTokens = (form.cc_emails || "").split(/[,;]/).map(s => s.trim()).filter(Boolean);
    const ccInvalid = ccTokens.filter(e => !e.includes("@"));
    if (ccInvalid.length > 0) { toast.error(`Correo en copia inválido: ${ccInvalid[0]}`); return; }
    setSaving(true);
    try {
      const payload = { ...form, cc_emails: ccTokens, destinatario_email: sup.email, destinatario_nombre: sup.nombre };
      if (editId) await axios.put(`${API}/reportes-programados/${editId}`, payload, getAuthHeaders());
      else await axios.post(`${API}/reportes-programados`, payload, getAuthHeaders());
      toast.success(editId ? "Configuración actualizada" : "Configuración creada");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const del = async (it) => {
    if (!window.confirm("¿Eliminar esta configuración de envío?")) return;
    try { await axios.delete(`${API}/reportes-programados/${it.id}`, getAuthHeaders()); toast.success("Configuración eliminada"); load(); }
    catch (e) { toast.error("Error al eliminar"); }
  };

  const toggleActivo = async (it) => {
    try {
      await axios.put(`${API}/reportes-programados/${it.id}`, { ...it, activo: it.activo === false }, getAuthHeaders());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al actualizar"); }
  };

  const sendNow = async (it) => {
    setSendingId(it.id);
    try {
      const r = await axios.post(`${API}/reportes-programados/${it.id}/enviar-ahora`, {}, getAuthHeaders());
      toast.success(`Reporte enviado a ${r.data.destinatario} (${r.data.desde} al ${r.data.hasta})`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); load(); }
    finally { setSendingId(null); }
  };

  const diasTxt = (ds) => (ds || []).map(d => `Día ${d}`).join(", ");

  const sendMesAnterior = async () => {
    if (!window.confirm("¿Enviar el reporte del MES ANTERIOR completo a todos los supervisores programados (configuraciones activas)?")) return;
    setSendingMesAnt(true);
    try {
      const r = await axios.post(`${API}/reportes-programados/enviar-mes-anterior`, {}, getAuthHeaders());
      const d = r.data;
      if (d.fallidos > 0) toast.warning(`Mes anterior (${d.desde} al ${d.hasta}): ${d.enviados} enviado(s), ${d.fallidos} fallido(s). Vea el historial.`);
      else toast.success(`Mes anterior (${d.desde} al ${d.hasta}): ${d.enviados} reporte(s) enviado(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
    finally { setSendingMesAnt(false); load(); }
  };

  return (
    <Card data-testid="envio-reportes-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" /> Envío programado de reportes</CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={sendMesAnterior} disabled={sendingMesAnt} data-testid="btn-envio-mes-anterior">
            {sendingMesAnt ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Envío mes anterior
          </Button>
          <Button onClick={openNew} data-testid="btn-nueva-config-envio"><Plus className="w-4 h-4 mr-1" /> Nueva configuración</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-800 p-3 text-sm text-indigo-900 dark:text-indigo-200">
          <p className="font-medium mb-1">¿Cómo funcionan los períodos?</p>
          <p>Los días configurados dividen el mes: con los días <strong>1 y 16</strong>, el día 16 se envían los registros del 01 al 15 del mes actual, y el día 1 se envían del 16 al último día del mes anterior. Si configura <strong>solo el día 1</strong>, se envía el mes anterior completo. El correo incluye el <strong>Reporte Diario</strong> y el <strong>Resumen por Empleado</strong> en Excel.</p>
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center" data-testid="envio-sin-configs">No hay configuraciones. Cree una para programar el envío automático de reportes.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="envio-configs-table">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-2 pr-3">Alcance</th>
                  <th className="py-2 pr-3">Destinatario</th>
                  <th className="py-2 pr-3">Días de envío</th>
                  <th className="py-2 pr-3">Hora</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id} className="border-b last:border-0" data-testid={`envio-config-row-${it.id}`}>
                    <td className="py-2 pr-3">
                      {it.tipo === "global"
                        ? <Badge className="bg-indigo-600 hover:bg-indigo-600">Global — Todos</Badge>
                        : <span className="font-medium">{it.departamento}</span>}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="font-medium">{it.destinatario_nombre || "—"}</div>
                      <div className="text-xs text-slate-500">{it.destinatario_email}</div>
                      {(it.cc_emails || []).length > 0 && (
                        <div className="text-xs text-slate-400" data-testid={`envio-cc-${it.id}`}>CC: {it.cc_emails.join(", ")}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3">{diasTxt(it.dias)}</td>
                    <td className="py-2 pr-3">{it.hora}</td>
                    <td className="py-2 pr-3">
                      <button type="button" onClick={() => toggleActivo(it)} data-testid={`envio-toggle-activo-${it.id}`}>
                        {it.activo !== false
                          ? <Badge className="bg-emerald-600 hover:bg-emerald-700 cursor-pointer"><CheckCircle2 className="w-3 h-3 mr-1" /> Activo</Badge>
                          : <Badge variant="secondary" className="cursor-pointer"><XCircle className="w-3 h-3 mr-1" /> Inactivo</Badge>}
                      </button>
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="mr-1" onClick={() => sendNow(it)} disabled={sendingId === it.id} data-testid={`envio-btn-enviar-ahora-${it.id}`}>
                        {sendingId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      </Button>
                      <Button size="sm" variant="outline" className="mr-1" onClick={() => openEdit(it)} data-testid={`envio-btn-editar-${it.id}`}><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => del(it)} data-testid={`envio-btn-eliminar-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="pt-4 border-t" data-testid="envio-historial-section">
          <h3 className="text-sm font-semibold flex items-center gap-2 mb-2"><Clock className="w-4 h-4 text-indigo-600" /> Historial de envíos</h3>
          {hist.length === 0 ? (
            <p className="text-sm text-slate-500 py-3 text-center" data-testid="envio-historial-vacio">Aún no se ha enviado ningún reporte.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="envio-historial-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-3">Fecha de envío</th>
                    <th className="py-2 pr-3">Alcance</th>
                    <th className="py-2 pr-3">Período</th>
                    <th className="py-2 pr-3">Destinatario</th>
                    <th className="py-2 pr-3">Origen</th>
                    <th className="py-2">Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  {hist.map(h => (
                    <tr key={h.id} className="border-b last:border-0" data-testid={`envio-hist-row-${h.id}`}>
                      <td className="py-2 pr-3 whitespace-nowrap">{new Date(h.created_at).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" })}</td>
                      <td className="py-2 pr-3">
                        {h.tipo === "global"
                          ? <Badge className="bg-indigo-600 hover:bg-indigo-600">Global — Todos</Badge>
                          : <span>{h.departamento}</span>}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{h.desde} al {h.hasta}</td>
                      <td className="py-2 pr-3">
                        <div>{h.destinatario_nombre || "—"}</div>
                        <div className="text-xs text-slate-500">{h.destinatario_email}</div>
                      </td>
                      <td className="py-2 pr-3">
                        <Badge variant="secondary">{h.origen === "manual" ? "Manual" : "Programado"}</Badge>
                      </td>
                      <td className="py-2">
                        {h.status === "exitoso"
                          ? <Badge className="bg-emerald-600 hover:bg-emerald-600"><CheckCircle2 className="w-3 h-3 mr-1" /> Exitoso</Badge>
                          : (
                            <div>
                              <Badge className="bg-red-600 hover:bg-red-600"><XCircle className="w-3 h-3 mr-1" /> Fallido</Badge>
                              {h.error && <div className="text-xs text-red-500 mt-0.5 max-w-[260px] truncate" title={h.error}>{h.error}</div>}
                            </div>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="envio-config-dialog">
          <DialogHeader><DialogTitle>{editId ? "Editar configuración" : "Nueva configuración de envío"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Alcance del reporte</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm(f => ({ ...f, tipo: v }))}>
                <SelectTrigger data-testid="envio-select-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="departamento">Departamento específico</SelectItem>
                  <SelectItem value="global">Global — Todos los departamentos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.tipo === "departamento" && (
              <div>
                <Label>Departamento</Label>
                <Select value={form.departamento} onValueChange={(v) => setForm(f => ({ ...f, departamento: v }))}>
                  <SelectTrigger data-testid="envio-select-departamento"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                  <SelectContent>
                    {deps.map(d => <SelectItem key={d.id} value={d.nombre}>{d.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{form.tipo === "global" ? "Destinatario (Director General)" : "Supervisor destinatario"}</Label>
              <Select value={form.destinatario_user_id} onValueChange={(v) => setForm(f => ({ ...f, destinatario_user_id: v }))}>
                <SelectTrigger data-testid="envio-select-destinatario"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                <SelectContent>
                  {sups.map(s => <SelectItem key={s.id} value={s.id}>{s.nombre} — {s.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Correos en copia (CC) — opcional</Label>
              <Input value={form.cc_emails} onChange={(e) => setForm(f => ({ ...f, cc_emails: e.target.value }))}
                placeholder="rh@empresa.com, otro@empresa.com" data-testid="envio-input-cc" />
              <p className="text-xs text-slate-500 mt-1">Separe varios correos con coma.</p>
            </div>
            <div>
              <Label>Días del mes para el envío</Label>
              <div className="grid grid-cols-7 gap-1 mt-1" data-testid="envio-dias-grid">
                {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                  <button key={d} type="button" onClick={() => toggleDia(d)} data-testid={`envio-dia-${d}`}
                    className={`h-8 rounded text-sm border transition-colors ${form.dias.includes(d) ? "bg-indigo-600 text-white border-indigo-600" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-indigo-400"}`}>
                    {d}
                  </button>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">Si selecciona un solo día, debe ser el día 1 (envía el mes anterior completo).</p>
            </div>
            <div className="flex items-end gap-4">
              <div>
                <Label>Hora de envío</Label>
                <Input type="time" value={form.hora} onChange={(e) => setForm(f => ({ ...f, hora: e.target.value }))} className="w-32" data-testid="envio-input-hora" />
              </div>
              <label className="flex items-center gap-2 pb-2 cursor-pointer">
                <Checkbox checked={form.activo} onCheckedChange={(v) => setForm(f => ({ ...f, activo: !!v }))} data-testid="envio-check-activo" />
                <span className="text-sm">Activo</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="envio-btn-guardar">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
